// src/components/partners/ProductPriceForm.jsx
import React, { useState, useEffect } from 'react';
import {
    Card, Row, Col, Table, Form, Button, Alert, InputGroup, Spinner
} from 'react-bootstrap';
import { RefreshCw, DollarSign, Plus, Save, Trash } from 'lucide-react';
import { getAllProducts } from '../../api/products';
import { getDistributorById, getDistributorPrices, setProductPrice } from '../../api/partner';

const ProductPriceForm = ({ distributorId, onCancel, onSuccess }) => {
    const [distributor, setDistributor] = useState(null);
    const [productPrices, setProductPrices] = useState([]);
    const [products, setProducts] = useState([]);
    const [availableProducts, setAvailableProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [selectedProductId, setSelectedProductId] = useState('');
    const [selectedProductName, setSelectedProductName] = useState('');
    const [newPrice, setNewPrice] = useState('');
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    useEffect(() => {
        loadData();
    }, [distributorId, refreshTrigger]);

    const loadData = async () => {
        try {
            setLoading(true);
            setError('');

            const distributorData = await getDistributorById(distributorId);
            setDistributor(distributorData);

            const pricesData = await getDistributorPrices(distributorId);
            setProductPrices(pricesData);

            const productsData = await getAllProducts();
            setProducts(productsData);

            const assignedProductIds = pricesData.map(price => price.product.id);
            const availableProds = productsData.filter(product => !assignedProductIds.includes(product.id));
            setAvailableProducts(availableProds);
        } catch (err) {
            setError('Erreur lors du chargement des données. Veuillez réessayer.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        setRefreshTrigger(prev => prev + 1);
        setSuccess('');
        setError('');
    };

    const handleProductSelection = (value) => {
        setSelectedProductName(value);
        const matchedProduct = availableProducts.find(product =>
            `${product.name} - ${product.sku}` === value
        );
        if (matchedProduct) {
            setSelectedProductId(matchedProduct.id);
        } else {
            setSelectedProductId('');
        }
    };

    const handlePriceChange = (e, priceId) => {
        const updatedPrice = e.target.value;
        setProductPrices(prev =>
            prev.map(price =>
                price.id === priceId ? { ...price, price: updatedPrice } : price
            )
        );
    };

    const handleSavePrice = async (price) => {
        try {
            setLoading(true);
            await setProductPrice(distributorId, price.product.id, parseFloat(price.price));
            setSuccess(`Prix mis à jour pour ${price.product.name}`);
            handleRefresh();
        } catch (err) {
            setError('Erreur lors de la mise à jour du prix. Veuillez réessayer.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleAddPrice = async () => {
        if (!selectedProductId || !newPrice || isNaN(parseFloat(newPrice)) || parseFloat(newPrice) < 0) {
            setError('Veuillez sélectionner un produit et entrer un prix valide');
            return;
        }

        try {
            setLoading(true);
            await setProductPrice(distributorId, selectedProductId, parseFloat(newPrice));
            setSuccess('Nouveau prix ajouté avec succès');
            setSelectedProductId('');
            setSelectedProductName('');
            setNewPrice('');
            handleRefresh();
        } catch (err) {
            setError('Erreur lors de l\'ajout du prix. Veuillez réessayer.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeletePrice = async (price) => {
        if (window.confirm(`Êtes-vous sûr de vouloir supprimer le prix spécifique pour ${price.product.name}?`)) {
            try {
                setLoading(true);
                await setProductPrice(distributorId, price.product.id, 0);
                setSuccess(`Prix supprimé pour ${price.product.name}`);
                handleRefresh();
            } catch (err) {
                setError('Erreur lors de la suppression du prix. Veuillez réessayer.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        }
    };

    if (loading && !distributor) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Chargement des données...</p>
            </div>
        );
    }

    return (
        <Card className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0 d-flex align-items-center">
                    <DollarSign size={20} className="me-2 text-success" />
                    Prix spécifiques - {distributor?.name}
                </h5>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleRefresh}
                    title="Rafraîchir"
                >
                    <RefreshCw size={16} />
                </Button>
            </Card.Header>
            <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                <Row>
                    <Col lg={5} className="mb-4">
                        <Card>
                            <Card.Header>
                                <h6 className="mb-0">Ajouter un nouveau prix</h6>
                            </Card.Header>
                            <Card.Body>
                                <Form.Group className="mb-3">
                                    <Form.Label>Produit</Form.Label>
                                    <Form.Control
                                        list="productOptions"
                                        value={selectedProductName}
                                        onChange={(e) => handleProductSelection(e.target.value)}
                                        placeholder="Saisir ou choisir un produit"
                                    />
                                    <datalist id="productOptions">
                                        {availableProducts.map(product => (
                                            <option
                                                key={product.id}
                                                value={`${product.name} - ${product.sku}`}
                                            >
                                                Prix de base: {product.basePrice.toFixed()}
                                            </option>
                                        ))}
                                    </datalist>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Prix spécifique</Form.Label>
                                    <InputGroup>
                                        
                                        <Form.Control
                                            type="number"
                                            onWheel={(e) => e.target.blur()}
                                            step="0.01"
                                            min="0"
                                            value={newPrice}
                                            onChange={(e) => setNewPrice(e.target.value)}
                                            placeholder="0.00"
                                        />
                                    </InputGroup>
                                </Form.Group>

                                <Button
                                    variant="primary"
                                    className="w-100"
                                    onClick={handleAddPrice}
                                    disabled={!selectedProductId || !newPrice || loading}
                                >
                                    <Plus size={16} className="me-1" />
                                    Ajouter ce prix
                                </Button>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={7}>
                        <Card>
                            <Card.Header>
                                <h6 className="mb-0">Prix spécifiques existants</h6>
                            </Card.Header>
                            <Card.Body>
                                {productPrices.length === 0 ? (
                                    <Alert variant="info">
                                        Aucun prix spécifique défini pour ce distributeur.
                                    </Alert>
                                ) : (
                                    <div className="table-responsive">
                                        <Table striped bordered hover>
                                            <thead>
                                                <tr>
                                                    <th>Produit</th>
                                                    <th>Prix de base</th>
                                                    <th>Prix spécifique</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {productPrices.map(price => (
                                                    <tr key={price.id}>
                                                        <td>
                                                            {price.product.name}
                                                            <div>
                                                                <small className="text-muted">
                                                                    Ref: {price.product.sku}
                                                                </small>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            {price.product.basePrice.toFixed()} F CFA
                                                        </td>
                                                        <td>
                                                            <InputGroup size="sm">
                                                                
                                                                <Form.Control
                                                                    type="number"
                                                                    onWheel={(e) => e.target.blur()}
                                                                    step="0.01"
                                                                    min="0"
                                                                    value={price.price}
                                                                    onChange={(e) => handlePriceChange(e, price.id)}
                                                                />
                                                            </InputGroup>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex">
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    className="me-1"
                                                                    onClick={() => handleSavePrice(price)}
                                                                    title="Enregistrer"
                                                                >
                                                                    <Save size={14} />
                                                                </Button>
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    onClick={() => handleDeletePrice(price)}
                                                                    title="Supprimer"
                                                                >
                                                                    <Trash size={14} />
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <div className="mt-4">
                    <Button variant="secondary" onClick={onCancel}>
                        Retour à la liste
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );
};

export default ProductPriceForm;
